export const OTA_SERVICE_UUID = '00008018-0000-1000-8000-00805f9b34fb';
export const OTA_FIRMWARE_CHAR_UUID = '00008020-0000-1000-8000-00805f9b34fb';
export const OTA_COMMAND_CHAR_UUID = '00008022-0000-1000-8000-00805f9b34fb';

export const OTA_SECTOR_SIZE = 4096;
export const OTA_PACKET_SIZE = 510;

export enum FirmwareAckCode {
  Ok = 0,
  CrcError = 1,
  IndexMismatch = 2,
  LengthError = 3,
}

export enum CommandAckCode {
  Ok = 0,
  SignatureError = 3,
  Rejected = 65535,
}

export type FirmwareAck = {
  ackIndex: number;
  code: FirmwareAckCode;
  rawCode: number;
};

export type CommandAck = {
  code: CommandAckCode;
  rawCode: number;
  opcode: number;
};

export type FirmwarePacket = {
  packet: Uint8Array;
  isLastInSector: boolean;
  sectorIndex: number;
  sequence: number;
};

export type FirmwarePacketOptions = {
  sectorSize?: number;
  packetSize?: number;
};

export function crc16(init: number, data: Uint8Array): number {
  let crc = init;
  for (let i = 0; i < data.length; i += 1) {
    crc ^= data[i] << 8;
    for (let j = 0; j < 8; j += 1) {
      if (crc & 0x8000) {
        crc = ((crc << 1) ^ 0x1021) & 0xffff;
      } else {
        crc = (crc << 1) & 0xffff;
      }
    }
  }
  return crc & 0xffff;
}

export function verifyAckCrc(pkt20: DataView | Uint8Array): boolean {
  const view = pkt20 instanceof DataView
    ? pkt20
    : new DataView(pkt20.buffer, pkt20.byteOffset, pkt20.byteLength);
  if (view.byteLength < 20) {
    return false;
  }
  const payload = new Uint8Array(view.buffer, view.byteOffset, 18);
  const computed = crc16(0, payload);
  const received = view.getUint16(18, true);
  return computed === received;
}

export function buildStartCommand(fileSize: number): Uint8Array {
  if (!Number.isFinite(fileSize) || fileSize < 0) {
    throw new Error('Invalid file size for OTA start command.');
  }
  const buffer = new Uint8Array(20);
  buffer[0] = 0x01;
  buffer[1] = 0x00;
  const view = new DataView(buffer.buffer);
  view.setUint32(2, fileSize, true);
  view.setUint16(18, crc16(0, buffer.slice(0, 18)), true);
  return buffer;
}

export function buildStopCommand(): Uint8Array {
  const buffer = new Uint8Array(20);
  buffer[0] = 0x02;
  buffer[1] = 0x00;
  const view = new DataView(buffer.buffer);
  view.setUint16(18, crc16(0, buffer.slice(0, 18)), true);
  return buffer;
}

export function* makeSectorPackets(
  sectorData: Uint8Array,
  sectorIndex: number,
  opts: FirmwarePacketOptions = {}
): Generator<FirmwarePacket> {
  const packetSize = opts.packetSize ?? OTA_PACKET_SIZE;
  const payloadBudget = packetSize - 3;
  let offset = 0;
  let sequence = 0;
  let crc = 0;

  while (offset < sectorData.length) {
    const remaining = sectorData.length - offset;
    const chunkSize = Math.min(payloadBudget, remaining);
    const chunk = sectorData.subarray(offset, offset + chunkSize);
    offset += chunkSize;
    const isLast = offset >= sectorData.length;
    crc = crc16(crc, chunk);

    const packetLength = 3 + chunk.length + (isLast ? 2 : 0);
    const packet = new Uint8Array(packetLength);
    packet[0] = sectorIndex & 0xff;
    packet[1] = (sectorIndex >> 8) & 0xff;
    packet[2] = isLast ? 0xff : (sequence & 0xff);
    packet.set(chunk, 3);

    if (isLast) {
      packet[packet.length - 2] = crc & 0xff;
      packet[packet.length - 1] = (crc >> 8) & 0xff;
    }

    yield {
      packet,
      isLastInSector: isLast,
      sectorIndex,
      sequence,
    };
    sequence += 1;
  }
}

export function* makeFirmwarePackets(
  file: ArrayBuffer,
  opts: FirmwarePacketOptions = {}
): Generator<FirmwarePacket> {
  const sectorSize = opts.sectorSize ?? OTA_SECTOR_SIZE;
  const fileBytes = new Uint8Array(file);
  const totalSectors = Math.ceil(fileBytes.length / sectorSize);

  for (let sectorIndex = 0; sectorIndex < totalSectors; sectorIndex += 1) {
    const start = sectorIndex * sectorSize;
    const end = Math.min(fileBytes.length, start + sectorSize);
    const sector = fileBytes.subarray(start, end);
    yield* makeSectorPackets(sector, sectorIndex, opts);
  }
}

export function parseFirmwareAck(view: DataView): FirmwareAck {
  if (view.byteLength < 4) {
    return { ackIndex: 0, code: FirmwareAckCode.CrcError, rawCode: 1 };
  }
  const ackIndex = view.getUint16(0, true);
  const fwAns = view.getUint16(2, true);
  let code = FirmwareAckCode.Ok;
  if (fwAns === FirmwareAckCode.CrcError) {
    code = FirmwareAckCode.CrcError;
  } else if (fwAns === FirmwareAckCode.IndexMismatch) {
    code = FirmwareAckCode.IndexMismatch;
  } else if (fwAns === FirmwareAckCode.LengthError) {
    code = FirmwareAckCode.LengthError;
  }
  return { ackIndex, code, rawCode: fwAns };
}

export function parseCommandAck(view: DataView): CommandAck | null {
  if (view.byteLength < 6) {
    return null;
  }
  const opcode = view.getUint16(0, true);
  if (opcode !== 3) {
    return null;
  }
  const ans = view.getUint16(4, true);
  let code = CommandAckCode.Rejected;
  if (ans === CommandAckCode.Ok) {
    code = CommandAckCode.Ok;
  } else if (ans === CommandAckCode.SignatureError) {
    code = CommandAckCode.SignatureError;
  }
  return { code, rawCode: ans, opcode };
}
